<template>
  <div class="accomodation-booking">
    <NuxtLink
      :to="to"
      class="button is-primary is-spaced"
      :class="{ 'is-loading': isLoading }"
    >
      <span>{{ label }}</span>
      <SpriteSymbol name="chevron-right" />
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'

const props = defineProps<{
  nid: string
}>()

const { getQueryVariables } = useHotelAvailabilityState()

const { $texts } = useEasyTexts()

// Does not need to be reactive.
const key = 'hotelAvailability_' + props.nid

const variables = computed(() => getQueryVariables(props.nid))

const { data: cheapeastRate, status } = await useLazyAsyncData(
  key,
  async () => {
    if (!variables.value) {
      return ''
    }

    const data = await useGraphqlQuery(
      'checkHotelAvailability',
      variables.value,
      {
        graphqlCaching: {
          client: true,
        },
      },
    )

    const node = data.data.entityById
    if (!node || !('getHotelAvailability' in node)) {
      return ''
    }
    const availability = node.getHotelAvailability

    if (!availability) {
      return ''
    }

    if (availability.error || !availability.items) {
      return ''
    }

    const cheapest = availability.items[0]

    if (!cheapest) {
      return ''
    }

    return cheapest.totalRate
  },
  {
    server: false,
  },
)

const isLoading = computed(() => status.value === 'pending')

const label = computed(() => {
  if (cheapeastRate.value) {
    return $texts('bookingButton').replace(
      '@price',
      cheapeastRate.value,
    )
  }

  return $texts('checkAvailability')
})

const to = computed<RouteLocationRaw>(() => {
  return {
    name: 'hotel-availability',
    params: {
      nid: props.nid,
    },
    query: {
      // When we found a cheapest rate, this query param makes sure the page
      // will load the results on mounted.
      immediate: cheapeastRate.value ? 'true' : undefined,
    },
  }
})
</script>

<style lang="postcss">
.accomodation-booking {
  .button {
    @apply w-full;
  }
}
.accomodation-booking-traveldates {
  @apply mt-size-7;
  .title {
    @apply mb-0;
  }
}
</style>
